/**
 * 1.8.0 genealogy in the Related tab: Made from (what PRISM recorded when the asset was made: read-only, grouped by
 * source, each use with its track, ranges and sequence, and while rights are on the rights then and now) and Used in
 * (the assets made with this one). Always shown, whether or not rights management is on (rule 5).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import type { AssetSourceDTO, GenealogyDTO, GenealogyUsedInDTO, RightsSummary } from '@prism/contracts';
import { useGenealogy } from '../api/hooks';
import { duration, relative } from '../lib/format';
import { RightsMark } from '../components/RightsMark';
import { rightsStateText } from '../lib/rights';
import { groupMadeFrom, hiddenText } from './genealogy';

const range = (a: number | null, b: number | null) => (a === null || b === null ? '' : `${duration(a)}–${duration(b)}`);

function RightsWord({ r, label }: { r: RightsSummary | null | undefined; label: string }) {
  if (r === undefined) return null;
  return <span className="faint" data-rights={label}>{label}: {r ? rightsStateText(r) : 'not recorded'}</span>;
}

function Use({ u }: { u: AssetSourceDTO }) {
  return (
    <div className="genealogy-use" data-testid="genealogy-use" data-track={u.track}>
      <span className="pill mono">{u.track}</span>
      {u.recordInTicks !== null && <span className="mono">at {range(u.recordInTicks, u.recordOutTicks)}</span>}
      {u.kind !== 'graphic' && u.sourceInTicks !== null && <span className="mono faint">source {range(u.sourceInTicks, u.sourceOutTicks)}</span>}
      {u.sequence.name && (u.sequence.id
        ? <Link to={`/create/${u.sequence.id}`} className="faint">from {u.sequence.name}</Link>
        : <span className="faint">from {u.sequence.name}</span>)}
      <RightsWord r={u.rightsThen} label="Then" />
      <RightsWord r={u.rightsNow} label="Now" />
    </div>
  );
}

/** Made from, grouped by source. */
export function MadeFrom({ data, showRights = true }: { data: GenealogyDTO; showRights?: boolean }) {
  const groups = groupMadeFrom(data.madeFrom);
  if (!groups.length) return null;
  return (
    <div data-testid="genealogy-made-from">
      <p className="section-label genealogy-label"><Lock size={11} aria-hidden /> Made from <span className="faint">· Recorded by PRISM</span></p>
      {groups.map((g) => (
        <div className="genealogy-source" key={g.key} data-testid="genealogy-source" data-asset-id={g.assetId ?? undefined}>
          <div className="genealogy-head">
            <Lock size={12} className="faint" aria-label="Recorded by PRISM; it can’t be removed" />
            {g.assetId && !g.purged ? <Link to={`/assets/${g.assetId}`} style={{ fontWeight: 550 }}>{g.title}</Link> : <span style={{ fontWeight: 550 }}>{g.title}</span>}
            {g.code && <span className="mono faint">{g.code}</span>}
            {g.purged && <span className="pill" data-testid="genealogy-purged">Purged</span>}
            {showRights && <RightsMark rights={g.uses[0]?.rightsNow} />}
            <span className="faint" style={{ marginLeft: 'auto' }}>{g.tracks.join(', ')} · {g.uses.length} use{g.uses.length === 1 ? '' : 's'}</span>
          </div>
          {g.uses.map((u) => <Use key={u.id} u={u} />)}
        </div>
      ))}
    </div>
  );
}

function UsedInRow({ u }: { u: GenealogyUsedInDTO }) {
  return (
    <div className="loc-row" data-testid="genealogy-used-in-item" data-asset-id={u.assetId}>
      {u.thumbnailUrl ? <img className="mini-thumb" src={u.thumbnailUrl} alt="" loading="lazy" /> : <Lock size={13} className="faint" />}
      <Link to={`/assets/${u.assetId}`} style={{ fontWeight: 550 }}>{u.title}</Link>
      {u.code && <span className="mono faint">{u.code}</span>}
      <RightsMark rights={u.rights} />
      <span className="key">{u.uses} use{u.uses === 1 ? '' : 's'} · {relative(u.createdAt)}</span>
    </div>
  );
}

/**
 * Made from, Used in (paged) and the hidden count, for the Related tab. Nothing while the genealogy is empty or its
 * route doesn't answer (an older server). Only the first page carries Made from, the hidden count and usedInCount;
 * further pages carry Used in alone.
 */
export function GenealogySections({ assetId }: { assetId: string }) {
  const first = useGenealogy(assetId);
  const [cursors, setCursors] = useState<string[]>([]);
  if (!first.data) return null;
  const d = first.data;
  const lastCursor = cursors.length ? null : d.nextCursor;
  if (!d.madeFrom.length && !d.usedIn.length && !d.hiddenCount) return null;
  return (
    <div className="genealogy" data-testid="genealogy">
      <MadeFrom data={d} />
      {(d.usedIn.length > 0 || cursors.length > 0) && (
        <div data-testid="genealogy-used-in">
          <p className="section-label genealogy-label"><Lock size={11} aria-hidden /> Used in <span className="faint" data-testid="genealogy-used-in-count">· {d.usedInCount ?? d.usedIn.length} asset{(d.usedInCount ?? d.usedIn.length) === 1 ? '' : 's'} · Recorded by PRISM</span></p>
          {d.usedIn.map((u) => <UsedInRow key={u.assetId} u={u} />)}
          {cursors.map((c, i) => <UsedInPage key={c} assetId={assetId} cursor={c} onMore={i === cursors.length - 1 ? (next) => setCursors([...cursors, next]) : undefined} />)}
          {lastCursor && <button className="btn ghost sm" onClick={() => setCursors([lastCursor])} data-testid="genealogy-more">Show more</button>}
        </div>
      )}
      {d.hiddenCount > 0 && <p className="faint" style={{ margin: 0 }} data-testid="genealogy-hidden"><Lock size={11} /> {hiddenText(d.hiddenCount)}</p>}
    </div>
  );
}

/** A further page of Used in. */
function UsedInPage({ assetId, cursor, onMore }: { assetId: string; cursor: string; onMore?: (next: string) => void }) {
  const q = useGenealogy(assetId, cursor);
  if (!q.data) return null;
  const next = q.data.nextCursor;
  return (
    <>
      {q.data.usedIn.map((u) => <UsedInRow key={u.assetId} u={u} />)}
      {onMore && next && <button className="btn ghost sm" onClick={() => onMore(next)} data-testid="genealogy-more">Show more</button>}
    </>
  );
}
